import { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCopilotAction, useCopilotContext } from '@copilotkit/react-core'
import { F0HILActionConfirmation } from '@factorialco/f0-react'

import { useActivities } from 'modules/core/lib/activities'
import { getActivity, type ActivityCatalog } from 'modules/core/lib/activities/catalog'
import type { SerializableProps } from 'modules/core/lib/activities/types'
import i18n from 'modules/core/lib/i18n'
import { findOneCreateAction, useOneActionNames } from 'modules/core/lib/oneCreateActions'
import {
  OneCreationActionResolver,
  isNavigationResult,
} from 'modules/core/lib/oneCreateActions/types'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'

const OneCreateActionExecutor: FC<{
  useOneActionResolver: OneCreationActionResolver
  data: unknown
  summary: string
  respond: (message: string) => void
}> = ({ useOneActionResolver, data, summary, respond }) => {
  const { resolver } = useOneActionResolver()
  const { threadId } = useCopilotContext()
  const navigate = useNavigate()

  const confirmAction = async () => {
    const result = await resolver(data, threadId)

    if (result === undefined) {
      return respond('Something went wrong, we could not handle the request.')
    } else if (isNavigationResult(result)) {
      let responseMessage = `Tool executed successfully, redirecting user to ${
        result.to
      } with the following data: ${JSON.stringify(result.data)}`
      if (result.followUp) {
        responseMessage += `\n${result.followUp}`
      }
      navigate(result.to, { state: { oneCreationData: result.data, threadId } })
      respond(responseMessage)
    } else if (result.success) {
      return respond(`Entity created successfully: ${JSON.stringify(result.data)}`)
    } else {
      return respond(`Failed to create entity: ${JSON.stringify(result?.errors)}`)
    }
  }

  return (
    <F0HILActionConfirmation
      text={summary}
      confirmationText={i18n.t('common.confirm')}
      onConfirm={confirmAction}
      cancelText={i18n.t('common.cancel')}
      onCancel={() => respond('Action cancelled')}
    />
  )
}

function isRecordOfUnknown(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

function isSerializableProps(value: unknown): value is SerializableProps {
  return isRecordOfUnknown(value)
}

function parseActivityData(data: unknown): SerializableProps | undefined {
  if (!isSerializableProps(data) || Object.keys(data).length === 0) return undefined
  return data
}

/** Validates activity id from Copilot (string) and returns ActivityCatalog when the activity exists. */
function parseActivityId(id: string): ActivityCatalog | null {
  // ActivityCatalog is a union of string literals; we must assert after getActivity validates.
  // eslint-disable-next-line no-type-assertion/no-type-assertion -- Copilot passes string; catalog is typed as literal union
  const catalogId = id as ActivityCatalog
  return getActivity(catalogId) ? catalogId : null
}

const respondToMutationApproval = (
  respond: (message: string) => void,
  approvalId: string,
  status: 'confirmed' | 'rejected',
  runId?: string
) => {
  respond(JSON.stringify({ approvalId, status, ...(runId ? { runId } : {}) }))
}

export const AiAgentGlobalActions = () => {
  const navigate = useNavigate()
  const { openActivity } = useActivities()
  const availableActions = useOneActionNames()
  const createEntityAvailable = availableActions.length > 0 ? 'remote' : 'disabled'

  useCopilotAction({
    name: 'notifyMutationExecuted',
    available: 'remote',
    followUp: false,
    description: 'Notify the frontend that a backend-executed mutation has completed.',
    parameters: [
      {
        name: 'operationName',
        description: 'The GraphQL mutation operation name.',
        required: true,
      },
      {
        name: 'variables',
        description: 'The GraphQL mutation variables used to execute the mutation.',
        type: 'object',
        required: false,
      },
      {
        name: 'result',
        description: 'The raw GraphQL mutation result payload.',
        type: 'object',
        required: false,
      },
    ],
    handler: async ({ operationName, variables, result }) => {
      if (!operationName) {
        return 'Mutation notification skipped because operationName is missing.'
      }

      dispatch({
        type: EventTypes.ONE_MUTATION_EXECUTED,
        data: {
          operationName,
          variables,
          result,
        },
      })

      return 'Mutation notification dispatched successfully.'
    },
  })

  useCopilotAction({
    name: 'openActivity',
    available: 'remote',
    followUp: false,
    description:
      'Open an in-app activity (overlay/modal). Use when the user asks to open a specific overlay, e.g. team status by clock-in state, company links edit, or other activities. Activity IDs are dot-separated, e.g. core.dashboardTeamStatus (takes data: { status: "should_not_be_working" | "missing_clock_in" | "working" }), core.dashboardCompanyLinksEdit.',
    parameters: [
      {
        name: 'activityId',
        description:
          'The activity ID (e.g. core.dashboardTeamStatus, core.dashboardCompanyLinksEdit).',
        required: true,
      },
      {
        name: 'data',
        description:
          'Optional JSON-serializable data for the activity. For core.dashboardTeamStatus use { status: "should_not_be_working" | "missing_clock_in" | "working" }. For richer previews, include the data the activity should render instead of expecting the frontend to look it up again.',
        required: false,
      },
    ],
    handler: async ({ activityId, data }) => {
      const catalogId = parseActivityId(activityId)
      if (!catalogId) return `Unknown activity: ${activityId}.`
      const params = parseActivityData(data)
      openActivity(catalogId, params)
    },
  })

  useCopilotAction({
    name: 'navigate',
    available: 'remote',
    description:
      'Navigate the user to a given path or URL within the application. Use when the user asks to go to a specific page.',
    parameters: [
      {
        name: 'path',
        description: 'The path to navigate to (e.g. /settings, /team).',
        required: true,
      },
      {
        name: 'followUp',
        description: 'A follow-up message to send after the navigation.',
        required: false,
      },
    ],
    handler: async ({ path, followUp }) => {
      navigate(path)

      return 'Navigation successful' + (followUp ? `\n${followUp}` : '')
    },
  })

  useCopilotAction({
    name: 'createEntity',
    description:
      'This action is only called when the user wants to create an entity and we have all the necessary data',
    followUp: true,
    available: createEntityAvailable,
    parameters: [
      {
        name: 'entityName',
        description: 'The name of the entity to create.',
        required: true,
        enum: availableActions,
      },
      {
        name: 'data',
        description: 'The data to create the entity with.',
        required: true,
      },
      {
        name: 'summary',
        description: 'A friendly summary with the data that will be used to create the entity.',
        required: true,
      },
    ],
    renderAndWaitForResponse: ({ status, args, respond }) => {
      if (status !== 'executing') {
        return <></>
      }

      const useOneActionResolver = findOneCreateAction(args.entityName)?.actionResolver

      if (!useOneActionResolver) {
        respond(
          `Something went wrong, the application is not ready to handle this request, the given entity name is not valid. The only valid names are: ${availableActions.join(
            ', '
          )}.`
        )
        return <></>
      }

      return (
        <OneCreateActionExecutor
          useOneActionResolver={useOneActionResolver}
          data={args.data}
          summary={args.summary}
          respond={respond}
        />
      )
    },
  })

  useCopilotAction({
    name: 'approveMutation',
    description:
      'This action is only called when the user needs to approve or reject a mutation that is ready to run.',
    followUp: true,
    available: 'remote',
    parameters: [
      {
        name: 'approvalId',
        description: 'The unique identifier of the approval request.',
        required: true,
      },
      {
        name: 'runId',
        description: 'The workflow run id that must be resumed after approval.',
        required: true,
      },
    ],
    renderAndWaitForResponse: ({ status, args, respond }) => {
      if (status !== 'executing') {
        return <></>
      }

      return (
        <F0HILActionConfirmation
          confirmationText={i18n.t('common.confirm')}
          onConfirm={() =>
            respondToMutationApproval(respond, args.approvalId, 'confirmed', args.runId)
          }
          cancelText={i18n.t('common.cancel')}
          onCancel={() =>
            respondToMutationApproval(respond, args.approvalId, 'rejected', args.runId)
          }
        />
      )
    },
  })

  useCopilotAction({
    name: 'setLocalStorage',
    available: 'remote',
    description:
      'Set a value in the local storage. Use when the user asks to set a value in the local storage.',
    parameters: [
      {
        name: 'key',
        description: 'The key to set in the local storage.',
        required: true,
      },
      {
        name: 'value',
        description: 'The value to set in the local storage.',
        required: true,
      },
    ],
    followUp: false,
    handler: async ({ key, value }) => {
      localStorage.setItem(key, value)
    },
  })

  return null
}
